/* Developer mailbox — mock mode only. Lists the emails the in-browser MockProvider "sent" (verification, reset,
 * notifications) so the demo can follow links without a real inbox. Never rendered with the real API. */
import "../../account/account.css";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useData } from "../../../data";
import type { DevMail } from "../../../data/mock";
import { useI18n } from "../../../i18n";
import { Button, Chip, Icon } from "../../../ui";

type WithMailbox = { devMailbox(): DevMail[]; clearMailbox(): void };
const hasMailbox = (p: unknown): p is WithMailbox => !!p && typeof (p as WithMailbox).devMailbox === "function";

export default function DevMailbox() {
  const p = useData();
  const { L } = useI18n();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [mails, setMails] = useState<DevMail[]>([]);
  const enabled = p.mode === "mock" && hasMailbox(p);
  const refresh = useCallback(() => { if (enabled) setMails((p as unknown as WithMailbox).devMailbox()); }, [enabled, p]);
  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 1000);
    return () => clearInterval(t);
  }, [refresh]);
  // nothing to show yet: stay out of the way until the demo sends its first email
  if (!enabled || (!open && mails.length === 0)) return null;
  const follow = (link: string) => {
    const i = link.indexOf("#/");
    if (i >= 0) { setOpen(false); nav(link.slice(i + 1)); } else window.open(link, "_blank", "noopener");
  };
  return (
    <div className="devmail" data-testid="dev-mailbox">
      <div className="row" style={{ justifyContent: "flex-end" }}>
        <Button variant="sm" icon="mail" className="devmail-toggle" aria-expanded={open} onClick={() => { refresh(); setOpen(!open); }} data-testid="dev-mailbox-toggle">
          {L("Dev mailbox", "صندوق المطور")} <span className="cnt" data-testid="dev-mailbox-count">{mails.length}</span>
        </Button>
      </div>
      {open && (
        <div className="devmail-panel" role="region" aria-label={L("Dev mailbox", "صندوق المطور")}>
          <div className="row between">
            <b>{L("Emails sent in this demo", "الرسائل المرسلة في هذا العرض")}</b>
            {mails.length > 0 && <Button variant="ghost sm" onClick={() => { (p as unknown as WithMailbox).clearMailbox(); refresh(); }} data-testid="dev-mailbox-clear">{L("Clear", "مسح")}</Button>}
          </div>
          <p className="xs muted" style={{ margin: "4px 0 8px" }}>{L("Demo only — real deployments deliver these to the recipient's inbox.", "للعرض فقط — في التشغيل الفعلي تصل هذه الرسائل إلى بريد المستلم.")}</p>
          {mails.length === 0 ? <p className="sm muted" data-testid="dev-mailbox-empty">{L("No emails yet.", "لا توجد رسائل بعد.")}</p> : mails.map((m) => (
            <div key={m.id} className="devmail-item" data-testid={"dev-mail-" + m.template}>
              <div className="row between nw"><b className="sm" dir="auto">{m.subject}</b><Chip tone={m.status === "sent" ? "ok" : m.status === "suppressed" ? "warn" : "info"}>{m.status}</Chip></div>
              <span className="xs muted" dir="ltr">{m.to} · {m.createdAt.slice(11, 16)}</span>
              {m.links.map((l) => (
                <a key={l} href={l} className="lnk" onClick={(e) => { e.preventDefault(); follow(l); }} data-testid={"dev-mail-link-" + m.template}>
                  <Icon name="external" className="s" /> {l.replace(/^https?:\/\/[^/]+/, "")}
                </a>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
